import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import Select from '../components/Select';
import { NtIcon } from '../components/NotionUI';
import { getMeeting, updateMeeting, summarizeMeeting, getProjects, createProjectTask, errorMessage } from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';
import { fmtDateTime } from '../utils/format';
import { STATUS_TONE } from './Meetings';

const toDraft = (m) => ({
  title: m.title || '',
  participants: m.participants || '',
  summary: m.summary || '',
  decisions: m.decisions || [],
  actions: m.actions || [],
  transcript: m.transcript || '',
});

const wordCount = (text) => (text.trim() ? text.trim().split(/\s+/).length : 0);

function MeetingDetail({ user, onLogout }) {
  const { t } = useLanguage();
  const { id } = useParams();
  const navigate = useNavigate();
  const [meeting, setMeeting] = useState(null);
  const [draft, setDraft] = useState(null);
  const [tab, setTab] = useState('notes');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [creating, setCreating] = useState(false);

  const apply = useCallback((m, keepDraft = false) => {
    setMeeting(m);
    if (!keepDraft) setDraft(toDraft(m));
  }, []);

  const load = useCallback(async () => {
    try {
      const data = await getMeeting(id);
      apply(data.meeting);
    } catch (err) {
      setError(errorMessage(err, t('meetings.loadError')));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    load();
    getProjects('active').then((d) => {
      const list = d.projects || [];
      setProjects(list);
      if (list[0]) setProjectId(String(list[0].id));
    }).catch(() => {});
  }, [load]);

  // Mentre l'elaborazione è in corso si interroga il server; a fine lavoro si aggiornano note e trascrizione.
  const processing = meeting?.status === 'processing';
  useEffect(() => {
    if (!processing) return undefined;
    const timer = setInterval(async () => {
      try {
        const data = await getMeeting(id);
        setMeeting(data.meeting);
        if (data.meeting.status !== 'processing') setDraft(toDraft(data.meeting));
      } catch (err) {
        /* si riprova al giro successivo */
      }
    }, 2500);
    return () => clearInterval(timer);
  }, [processing, id]);

  const dirty = useMemo(() => meeting && draft && JSON.stringify(toDraft(meeting)) !== JSON.stringify(draft), [meeting, draft]);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const data = await updateMeeting(id, draft);
      apply(data.meeting);
      setNotice(t('meetings.saved'));
      setTimeout(() => setNotice(''), 2000);
    } catch (err) {
      setError(errorMessage(err, t('meetings.saveError')));
    } finally {
      setSaving(false);
    }
  };

  const regenerate = async () => {
    if (meeting.summary && !window.confirm(t('meetings.regenerateConfirm'))) return;
    setError('');
    try {
      if (dirty) await updateMeeting(id, { transcript: draft.transcript, title: draft.title, participants: draft.participants });
      const data = await summarizeMeeting(id);
      apply(data.meeting);
      setTab('notes');
    } catch (err) {
      setError(errorMessage(err, t('meetings.regenerateError')));
    }
  };

  const setAction = (index, patch) => setDraft((d) => ({ ...d, actions: d.actions.map((a, i) => (i === index ? { ...a, ...patch } : a)) }));
  const setDecision = (index, value) => setDraft((d) => ({ ...d, decisions: d.decisions.map((x, i) => (i === index ? value : x)) }));

  const asText = () => {
    const lines = [`# ${draft.title}`];
    if (meeting.meeting_date) lines.push(fmtDateTime(meeting.meeting_date));
    if (draft.participants) lines.push(`${t('meetings.fieldParticipants')}: ${draft.participants}`);
    lines.push('', `## ${t('meetings.summary')}`, draft.summary);
    if (draft.decisions.length) lines.push('', `## ${t('meetings.decisions')}`, ...draft.decisions.map((d) => `- ${d}`));
    if (draft.actions.length) {
      lines.push('', `## ${t('meetings.actions')}`, ...draft.actions.map((a) => `- [${a.done ? 'x' : ' '}] ${a.text}${a.owner ? ` (${a.owner})` : ''}${a.due ? ` — ${a.due}` : ''}`));
    }
    return lines.join('\n');
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(asText());
      setNotice(t('meetings.copied'));
    } catch (err) {
      setError(t('meetings.copyError'));
    }
    setTimeout(() => setNotice(''), 2000);
  };

  const createTasks = async () => {
    const pending = draft.actions.filter((a) => !a.done && a.text.trim());
    if (!projectId || pending.length === 0) return;
    setCreating(true);
    setError('');
    try {
      for (const action of pending) {
        const title = action.owner ? `${action.text} (${action.owner})` : action.text;
        // eslint-disable-next-line no-await-in-loop
        await createProjectTask(Number(projectId), { title });
      }
      setNotice(t('meetings.tasksCreated', { count: pending.length }));
      setTimeout(() => setNotice(''), 3500);
    } catch (err) {
      setError(errorMessage(err, t('meetings.tasksError')));
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return <Layout user={user} onLogout={onLogout}><div className="loading">{t('common.loading')}</div></Layout>;
  }
  if (!meeting || !draft) {
    return (
      <Layout user={user} onLogout={onLogout}>
        {error && <div className="error-message">{error}</div>}
        <button className="btn btn-secondary" type="button" onClick={() => navigate('/meetings')}>{t('meetings.backToList')}</button>
      </Layout>
    );
  }

  const ready = meeting.status === 'ready';

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="nt-page mt-page">
        <button className="tpl-back fd-back" type="button" onClick={() => navigate('/meetings')}>
          <NtIcon name="back" /> {t('meetings.backToList')}
        </button>

        <header className="mt-head">
          <input
            className="mt-title"
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            aria-label={t('meetings.fieldTitle')}
          />
          <div className="mt-head-meta">
            <span className={`mt-status mt-status-${STATUS_TONE[meeting.status] || 'idle'}`}>{t(`meetings.status_${meeting.status}`)}</span>
            <span>{fmtDateTime(meeting.meeting_date || meeting.created_at)}</span>
            <input
              className="mt-participants"
              value={draft.participants}
              onChange={(e) => setDraft({ ...draft, participants: e.target.value })}
              placeholder={t('meetings.participantsPlaceholder')}
              aria-label={t('meetings.fieldParticipants')}
            />
            {meeting.engine && <span className="mt-engine-tag">{meeting.engine}</span>}
          </div>
        </header>

        {error && <div className="error-message">{error}</div>}
        {notice && <div className="success-message">{notice}</div>}

        <div className="mt-tabs-row">
          <div className="pj-view-toggle" role="tablist">
            <button type="button" role="tab" aria-selected={tab === 'notes'} className={'pj-view-btn tk-view-btn' + (tab === 'notes' ? ' pj-view-active' : '')} onClick={() => setTab('notes')}>
              <NtIcon name="list" /> {t('meetings.tabNotes')}
            </button>
            <button type="button" role="tab" aria-selected={tab === 'transcript'} className={'pj-view-btn tk-view-btn' + (tab === 'transcript' ? ' pj-view-active' : '')} onClick={() => setTab('transcript')}>
              <NtIcon name="text" /> {t('meetings.tabTranscript')}
            </button>
          </div>
          {processing && (
            <span className="mt-wave" aria-hidden="true"><i /><i /><i /><i /><i /><i /><i /></span>
          )}
          <span className="tk-bar-spacer" />
          {ready && (
            <>
              <button type="button" className="nt-ghost-btn" onClick={copy}><NtIcon name="copy" /> {t('meetings.copy')}</button>
              <button type="button" className="nt-ghost-btn" onClick={regenerate}>↺ {t('meetings.regenerate')}</button>
            </>
          )}
        </div>

        {processing && (
          <div className="mt-processing">
            <strong>{t(`meetings.stage_${meeting.stage || 'summarizing'}`)}…</strong>
            <span>{t('meetings.processingHint')}</span>
          </div>
        )}

        {meeting.status === 'failed' && (
          <div className="mt-warning">
            <strong>{t('meetings.failedTitle')}</strong>
            <span>{meeting.error_message}</span>
            {draft.transcript.trim() && <button type="button" className="btn btn-sm" onClick={regenerate}>{t('meetings.retry')}</button>}
          </div>
        )}

        {tab === 'notes' ? (
          <div className="mt-notes">
            <section className="td-card">
              <h3 className="td-card-title">{t('meetings.summary')}</h3>
              <textarea
                className="mt-area"
                rows={6}
                value={draft.summary}
                onChange={(e) => setDraft({ ...draft, summary: e.target.value })}
                placeholder={processing ? '' : t('meetings.summaryEmpty')}
              />
            </section>

            <section className="td-card">
              <div className="td-card-head">
                <h3 className="td-card-title">{t('meetings.decisions')} ({draft.decisions.length})</h3>
                <button type="button" className="nt-ghost-btn" onClick={() => setDraft({ ...draft, decisions: [...draft.decisions, ''] })}>+ {t('meetings.add')}</button>
              </div>
              {draft.decisions.length === 0 && <p className="td-muted">{t('meetings.noDecisions')}</p>}
              {draft.decisions.map((d, i) => (
                <div key={i} className="mt-row">
                  <span className="mt-bullet" aria-hidden="true">◆</span>
                  <input value={d} onChange={(e) => setDecision(i, e.target.value)} />
                  <button type="button" className="tp-icon tp-icon-danger" onClick={() => setDraft({ ...draft, decisions: draft.decisions.filter((_, k) => k !== i) })} aria-label={t('common.delete')}>
                    <NtIcon name="trash" size={15} />
                  </button>
                </div>
              ))}
            </section>

            <section className="td-card">
              <div className="td-card-head">
                <h3 className="td-card-title">{t('meetings.actions')} ({draft.actions.length})</h3>
                <button type="button" className="nt-ghost-btn" onClick={() => setDraft({ ...draft, actions: [...draft.actions, { text: '', owner: '', due: '', done: false }] })}>+ {t('meetings.add')}</button>
              </div>
              {draft.actions.length === 0 && <p className="td-muted">{t('meetings.noActions')}</p>}
              {draft.actions.map((a, i) => (
                <div key={i} className={'mt-action' + (a.done ? ' mt-action-done' : '')}>
                  <input type="checkbox" className="mt-check" checked={a.done} onChange={(e) => setAction(i, { done: e.target.checked })} aria-label={t('meetings.markDone')} />
                  <input className="mt-action-text" value={a.text} onChange={(e) => setAction(i, { text: e.target.value })} />
                  <input className="mt-action-owner" value={a.owner} onChange={(e) => setAction(i, { owner: e.target.value })} placeholder={t('meetings.owner')} />
                  <input className="mt-action-due" value={a.due} onChange={(e) => setAction(i, { due: e.target.value })} placeholder={t('meetings.due')} />
                  <button type="button" className="tp-icon tp-icon-danger" onClick={() => setDraft({ ...draft, actions: draft.actions.filter((_, k) => k !== i) })} aria-label={t('common.delete')}>
                    <NtIcon name="trash" size={15} />
                  </button>
                </div>
              ))}
              {projects.length > 0 && draft.actions.some((a) => !a.done && a.text.trim()) && (
                <div className="mt-tasks">
                  <span className="td-muted">{t('meetings.createTasksIn')}</span>
                  <Select value={projectId} onChange={(e) => setProjectId(e.target.value)} aria-label={t('meetings.createTasksIn')}>
                    {projects.map((p) => <option key={p.id} value={p.id}>{p.icon ? `${p.icon} ${p.name}` : p.name}</option>)}
                  </Select>
                  <button type="button" className="btn btn-secondary btn-sm" disabled={creating} onClick={createTasks}>
                    {creating ? t('meetings.creating') : t('meetings.createTasks')}
                  </button>
                </div>
              )}
            </section>
          </div>
        ) : (
          <section className="td-card">
            <div className="td-card-head">
              <h3 className="td-card-title">{t('meetings.tabTranscript')}</h3>
              <span className="td-muted">{t('meetings.wordCount', { count: wordCount(draft.transcript) })}</span>
            </div>
            <textarea
              className="mt-area mt-area-transcript"
              rows={18}
              value={draft.transcript}
              onChange={(e) => setDraft({ ...draft, transcript: e.target.value })}
              placeholder={processing ? t('meetings.transcribingPlaceholder') : ''}
            />
            <p className="tp-hint">{t('meetings.transcriptHint')}</p>
            {draft.transcript.trim() && !processing && (
              <button type="button" className="btn btn-secondary btn-sm" onClick={regenerate}>↺ {t('meetings.regenerateFromTranscript')}</button>
            )}
          </section>
        )}

        {dirty && (
          <div className="tp-savebar">
            <button type="button" className="btn btn-secondary" onClick={() => setDraft(toDraft(meeting))}>{t('meetings.discard')}</button>
            <button type="button" className="btn" disabled={saving} onClick={save}>{saving ? t('common.saving') : t('common.save')}</button>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default MeetingDetail;
